import React, { useEffect, useState } from 'react';
import { Search, Trash2, Shield } from 'lucide-react';
import { adminAPI } from '../../api/admin';
import { User } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';

const CustomersAdminPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const { isSuperAdmin } = useAuth();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const res = await adminAPI.getAllUsers();
      if (res.success && res.data) setUsers(res.data);
    } catch {
      toast.error('Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateRole = async (id: string, role: string) => {
    try {
      await adminAPI.updateUserRole(id, role);
      toast.success('Role updated');
      fetchUsers();
    } catch {
      toast.error('Failed to update role');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await adminAPI.deleteUser(id);
      toast.success('User deleted');
      fetchUsers();
    } catch {
      toast.error('Failed to delete user');
    }
  };

  const filtered = users.filter(
    (u) =>
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      u.first_name.toLowerCase().includes(search.toLowerCase()) ||
      u.last_name.toLowerCase().includes(search.toLowerCase())
  );

  const roleColors: Record<string, string> = {
    customer: 'text-primary bg-light',
    admin: 'text-blue-600 bg-blue-50',
    super_admin: 'text-purple-600 bg-purple-50',
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs tracking-widest uppercase text-nude mb-1">Management</p>
        <h1 className="font-serif text-3xl text-primary">Customers</h1>
      </div>

      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nude" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="SEARCH CUSTOMERS"
          className="w-full bg-white border border-light pl-9 pr-4 py-3 text-xs tracking-widest outline-none focus:border-primary transition-colors"
        />
      </div>

      <div className="bg-white border border-light overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-light">
              {['Customer', 'Email', 'WhatsApp', 'Role', 'Verified', 'Joined', 'Actions'].map((h) => (
                <th key={h} className="px-6 py-3 text-left text-xs tracking-widest uppercase text-nude">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center">
                  <div className="skeleton h-6 w-48 mx-auto rounded" />
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-xs text-nude tracking-widest uppercase">
                  No customers found
                </td>
              </tr>
            ) : (
              filtered.map((user) => (
                <tr key={user.id} className="border-b border-light hover:bg-light/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary text-white flex items-center justify-center text-xs font-medium flex-shrink-0">
                        {user.first_name[0]}{user.last_name[0]}
                      </div>
                      <p className="text-xs font-medium">
                        {user.first_name} {user.last_name}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-xs text-nude">{user.email}</td>
                  <td className="px-6 py-4 text-xs">
                    {(user as any).phone ? (
                      <a
                        href={`https://wa.me/${(user as any).phone.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline"
                      >
                        {(user as any).phone}
                      </a>
                    ) : (
                      <span className="text-nude">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs tracking-widest uppercase px-2 py-1 rounded ${
                      roleColors[user.role] || 'text-primary bg-light'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs tracking-widest uppercase ${
                      user.is_verified ? 'text-green-600' : 'text-amber-600'
                    }`}>
                      {user.is_verified ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-xs text-nude">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      {isSuperAdmin && (
                        <>
                          <div className="relative group">
                            <button className="text-nude hover:text-primary transition-colors">
                              <Shield size={14} />
                            </button>
                            <div className="absolute right-0 top-6 bg-white border border-light shadow-lg w-36 z-10 hidden group-hover:block">
                              {['customer', 'admin', 'super_admin'].map((role) => (
                                <button
                                  key={role}
                                  onClick={() => handleUpdateRole(user.id, role)}
                                  className={`block w-full text-left px-4 py-2 text-xs tracking-widest uppercase hover:bg-light transition-colors ${
                                    user.role === role ? 'text-primary font-medium' : 'text-nude'
                                  }`}
                                >
                                  {role.replace('_', ' ')}
                                </button>
                              ))}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="text-nude hover:text-red-500 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CustomersAdminPage;